<script>
import {
  computed,
  defineComponent,
  onBeforeMount,
  ref,
  watch
} from "vue";
import {useMeta} from "vue-meta";
import TabMenu from "primevue/tabmenu";
import Paginator from "primevue/paginator";
import MainCard from "@/components/cards/MainCard";
import Breadcrumb from "@/components/Breadcrumb.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import SettingsMain from "./tabs/SettingsMain.vue";
import HintsMain from './tabs/HintsMain.vue'
import Editor from "primevue/editor";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import ImageCard from "@/components/cards/ImageCard.vue";
import Skeleton from "primevue/skeleton"
import Button from "primevue/button";
import {useRoute, useRouter} from "vue-router";
import { useApiCall } from "@/composables/useApiCall";
import {
  modalsApiCall,
  deleteModalsItemApiCall,
  settingsDocsApiCall,
} from "@/services/settings"
import SettingsStub from "@/views/settings/tabs/SettingsStub.vue";
import SettingsServices from '@/views/settings/tabs/SettingsServices.vue'
import DataTable from "primevue/datatable";
import Column from "primevue/column";

export default defineComponent({
  layout: { name: 'AdminLayout' },
  components: {
    Column, DataTable,
    SettingsStub,
    SettingsMain,
    ImageCard,
    Editor,
    Breadcrumb,
    MainCard,
    TabMenu,
    Paginator,
    ConfirmationModal,
    Button,
    Skeleton,
    HintsMain,
    SettingsServices,
  },
  setup() {
    const {t} = useI18n()
    useMeta({
      title: t('menu.website'),
    });
    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const activeTabIndex = ref(0);
    const isUpdated = ref(false);
    const errors = ref(null);
    // const modalsItems = ref([])
    const modalsItems = computed(() => modalsData.value?.data)
    const first = ref(0);

    const servicesItems = ref([])
    const activeTabComponent = computed(() => tabs.value[activeTabIndex.value]);
    const router = useRouter();
    const route = useRoute();
    const tabs = ref([
      {label: 'Инфо страницы приложения', component: 'main' },
      {label: 'Модальные окна', component: 'modals'},
      {label: 'Пояснения к графикам отчетов', component: 'hints'},
      {label: 'Заглушка', component: 'stub'},
      {label: 'Документы', component: 'documents'},
      {label: 'Услуги', component: 'services'},
    ]);

    const {
      isLoading: isModalsLoading,
      data: modalsData,
      error: modalsError,
      executeApiCall: getModalsAction,
    } = useApiCall(modalsApiCall, true)

    const {
      isLoading: isDeleteModalsItemLoading,
      data: deleteModalsItemsData,
      error: deleteModalsItemError,
      executeApiCall: deleteModalsItemAction,
    } = useApiCall(deleteModalsItemApiCall, true)

    const {
      isLoading: isSettingsDocsLoading,
      data: settingsDocsData,
      // error: settingsDocsError,
      executeApiCall: settingsDocsAction,
    } = useApiCall(settingsDocsApiCall, true)

    const deleteId = ref(0)

    const deleteItem = (id) => {
      deleteId.value = id
    }

    const changeVisibleModal = () => {
      deleteId.value = 0
    }

    const deleteCategory = async () => {
      try {
        await deleteModalsItemAction({id: deleteId.value})
        changeVisibleModal()
        if (first.value > 1 && modalsItems.value.data.length - 1 === 0) {
          first.value--
        }
        await findModals()
      } catch {
        console.error(deleteModalsItemError.value)
      }
    }

    watch(() => activeTabIndex.value,
        async () => {
          docsStatus.value = null
          if (!activeTabIndex.value) {
            await router.push({query: {}})
          } else {
            await router.push({query: {tab: activeTabComponent.value.component}})
          }
          if (activeTabComponent.value.component === 'modals') {
            await findModals()
          }
        }, {deep: true}
    )
    const isLoading = ref(false);

    const findModals = async () => {
      isLoading.value = true;
      const filterObject = {
        page: ((first.value / (modalsItems.value?.pagination?.per_page ?? 1)) + 1),
      };
      await getModalsAction(filterObject)
      isLoading.value = false;
      window.scrollTo(0, 0);
    };
    const docsStatus = ref(null)
    const sendDocumentForm = async () => {
      docsStatus.value = null
      try {
        await settingsDocsAction()
        if (settingsDocsData.value) {
          docsStatus.value = 'success';
        }
      } catch {
        docsStatus.value = 'error';
      }
    }

    watch((first), async () => {
      await findModals()
    });

    onBeforeMount(async () => {
      if (route.query?.tab) {
        activeTabIndex.value = tabs.value.findIndex(item => item.component === route.query.tab)
      }
      window.scrollTo(0, 0);
    })

    return {
      tabs,
      isUpdated,
      activeTabIndex,
      activeTabComponent,
      errors,
      t,
      isModalsLoading,
      servicesItems,
      route,
      lang,
      modalsItems,
      deleteId,
      deleteItem,
      changeVisibleModal,
      deleteCategory,
      isDeleteModalsItemLoading,
      first,
      isLoading,
      isSettingsDocsLoading,
      docsStatus,
      sendDocumentForm,
    };
  },
})
</script>

<template>
  <ConfirmationModal :visible="!!deleteId">
    <template #header>
      Удалить модальное окно
    </template>
    <template #default>
      <span>Вы уверены, что хотите удалить модальное окно? Это действие будет невозможно отменить</span>
    </template>
    <template #footer>
      <div class="flex justify-content-between">
        <Button label="Отменить" @click="changeVisibleModal" class="btn-primary-outlined font-light w-12"/>
        <Button label="Удалить" :loading="isDeleteModalsItemLoading" @click="deleteCategory" class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <div class="relative">
    <section class="py-2">
      <div class="flex justify-content-between" style="min-height: 48px">
        <h1 class="settings__title" style="min-height: 46px">{{ t('menu.settings') }}</h1>
        <router-link
            v-if="activeTabComponent.component === 'modals'"
            :to="{ name: 'modals-add'}"
            class="p-button btn-primary btn-fix-height settings__link"
        >{{ t('settings.modals.link') }}</router-link>
        <div v-if="activeTabComponent.component === 'documents'">
          <div v-if="docsStatus" class="documents__wrapper" :class="{error: docsStatus === 'error', success: docsStatus === 'success'}">
            <div class="documents__status">
              <img v-if="docsStatus === 'success'" src="@/assets/images/icons/check-mark.svg" alt="success">
              <img v-else src="@/assets/images/icons/warning.svg" alt="warning"/>
            </div>
            <span>{{docsStatus === 'success' ? 'Успешно' : 'Ошибка'}}</span>
          </div>
        </div>
      </div>
    </section>
    <section class="py-2 mb-3">
      <TabMenu v-model:active-index="activeTabIndex" :model="tabs" />
    </section>
    <template v-if="activeTabComponent.component === 'hints'">
      <HintsMain />
    </template>
    <template v-if="activeTabComponent.component === 'main'">
      <SettingsMain />
    </template>
    <template v-if="activeTabComponent.component === 'services'">
      <SettingsServices />
    </template>
    <template v-if="activeTabComponent.component === 'modals'">
      <div class="settings__modals">
        <DataTable :value="modalsItems?.data ? modalsItems.data : []" :loading="isLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
          <Column field="first_name" header="Название" style="width: 45%">
            <template #body="slotProps">
              <router-link
                  :to="{name: 'modals-item', params: {id: slotProps.data.id}}"
                  class="flex flex-column gap-3 settings__modals-item-link"
              >
                {{slotProps.data.title_ru}}
              </router-link>
            </template>
          </Column>
          <Column field="segments" header="Сегменты" style="width: 45%">
            <template #body="slotProps">
              <span class="settings__modals-item-segment">{{slotProps.data.segments}}</span>
            </template>
          </Column>

          <Column field="action" header="" style="width: 10%">
            <template #body="slotProps">
              <button @click="deleteItem(slotProps.data.id)" class="settings__modals-item-delete">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.2679 5.36049H14.7322C14.7322 4.76849 14.497 4.20074 14.0784 3.78213C13.6598 3.36352 13.092 3.12835 12.5 3.12835C11.908 3.12835 11.3403 3.36352 10.9217 3.78213C10.503 4.20074 10.2679 4.76849 10.2679 5.36049ZM8.92859 5.36049C8.92859 4.41329 9.30486 3.50488 9.97464 2.83511C10.6444 2.16534 11.5528 1.78906 12.5 1.78906C13.4472 1.78906 14.3556 2.16534 15.0254 2.83511C15.6952 3.50488 16.0714 4.41329 16.0714 5.36049H21.6518C21.8294 5.36049 21.9997 5.43104 22.1253 5.55662C22.2509 5.68221 22.3214 5.85253 22.3214 6.03013C22.3214 6.20773 22.2509 6.37806 22.1253 6.50364C21.9997 6.62923 21.8294 6.69978 21.6518 6.69978H20.4822L19.3956 19.7382C19.3165 20.6867 18.8839 21.5708 18.1835 22.2153C17.4831 22.8598 16.5661 23.2176 15.6143 23.2176H9.38573C8.43394 23.2176 7.51693 22.8598 6.81654 22.2153C6.11615 21.5708 5.68354 20.6867 5.60448 19.7382L4.51787 6.69978H3.34823C3.17063 6.69978 3.00031 6.62923 2.87472 6.50364C2.74914 6.37806 2.67859 6.20773 2.67859 6.03013C2.67859 5.85253 2.74914 5.68221 2.87472 5.55662C3.00031 5.43104 3.17063 5.36049 3.34823 5.36049H8.92859ZM6.9393 19.6266C6.99037 20.2403 7.27022 20.8125 7.72336 21.2296C8.17651 21.6467 8.76985 21.8782 9.38573 21.8783H15.6143C16.2302 21.8782 16.8235 21.6467 17.2767 21.2296C17.7298 20.8125 18.0097 20.2403 18.0607 19.6266L19.1393 6.69978H5.86162L6.9393 19.6266ZM10.4911 9.82478C10.6687 9.82478 10.839 9.89533 10.9646 10.0209C11.0902 10.1465 11.1607 10.3168 11.1607 10.4944V18.0837C11.1607 18.2613 11.0902 18.4316 10.9646 18.5572C10.839 18.6828 10.6687 18.7533 10.4911 18.7533C10.3135 18.7533 10.1432 18.6828 10.0176 18.5572C9.892 18.4316 9.82145 18.2613 9.82145 18.0837V10.4944C9.82145 10.3168 9.892 10.1465 10.0176 10.0209C10.1432 9.89533 10.3135 9.82478 10.4911 9.82478ZM15.1786 10.4944C15.1786 10.3168 15.108 10.1465 14.9825 10.0209C14.8569 9.89533 14.6865 9.82478 14.5089 9.82478C14.3313 9.82478 14.161 9.89533 14.0354 10.0209C13.9099 10.1465 13.8393 10.3168 13.8393 10.4944V18.0837C13.8393 18.2613 13.9099 18.4316 14.0354 18.5572C14.161 18.6828 14.3313 18.7533 14.5089 18.7533C14.6865 18.7533 14.8569 18.6828 14.9825 18.5572C15.108 18.4316 15.1786 18.2613 15.1786 18.0837V10.4944Z" fill="#E26B6B"/>
                </svg>
              </button>
            </template>
          </Column>
          <template #empty> Модальные окна отсутствуют </template>
        </DataTable>
        <Paginator
            v-model:first="first"
            :rows="modalsItems?.pagination?.per_page ?? 0"
            :totalRecords="modalsItems?.pagination?.total ?? 0"
            class="justify-content-start"
        ></Paginator>
      </div>
    </template>
    <template v-if="activeTabComponent.component === 'documents'">
      <div>
        <Button :disabled="isSettingsDocsLoading" label="Запросить неподписанные документы" style="height: 46px" class="btn-primary" @click="sendDocumentForm"/>
      </div>
    </template>
    <template v-if="activeTabComponent.component === 'stub'">
      <SettingsStub />
    </template>
  </div>

</template>
<style lang="scss" scoped>
::v-deep(.p-column-header-content) {
  font-family: "RF Dewi", sans-serif;
  justify-content: start !important;
}
::v-deep(.p-column-title) {
  font-weight: 600;
  font-size: 18px;
  line-height: 100%;
  text-align: start;
  padding-left: 4px;
}
.settings {
  &__title {
    min-height: 37px;
  }
  &__link {
    text-decoration: unset;
  }
  &__modals {
    background: white;
    &-item {
      display: grid;
      grid-template-columns: 1fr 1fr 114px;
      &-link {
        font-weight: 400;
        font-size: 18px;
        line-height: 100%;
        text-decoration: underline;
        color: #C3987A;
        text-align: left;
        padding-left: 4px;
      }
      &-segment {
        padding-left: 4px;
        display: flex;
        font-weight: 400;
        font-size: 18px;
        line-height: 100%;
        color: #333333;
        text-align: left;
      }
      &-delete {
        background: unset;
        border: none;
        cursor: pointer;
      }
    }
    &-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 300px;
      width: 100%;
      color: var(--color-black-40);
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
    }
  }

}
::v-deep(.p-menuitem-text) {
  white-space: nowrap;
}
::v-deep(.p-tabmenu .p-tabmenu-nav) {
  border-color: transparent;
  gap: 24px;
}
::v-deep(.p-tabmenu .p-tabmenu-nav .p-tabmenuitem .p-menuitem-link) {
  border-color: transparent;
  padding: 9px 0;
}
::v-deep(.p-menuitem-link) {
  box-shadow: unset !important;
}
::v-deep(.p-component-overlay) {
  background-color: rgba(255, 255, 255, 0.6);
}
::v-deep(.p-datatable-loading-icon) {
  path {
    fill: var(--color-primary);
  }
}
.documents__wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  border-radius: 4px;
  background-color: #FFFFFF;
  &.success {
    .documents__status {
      background: #9CC5E1;
    }
  }
}
.documents__status {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #E26B6B;
  width: 24px;
  height: 24px;
}
</style>